import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { PreferredSide } from '@sorare/core/src/__generated__/globalTypes';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';

import type { HandInfoChip_BaseballPlayer } from './__generated__/index.graphql.ts';

const messages = {
  pitching: {
    [PreferredSide.BOTH]: defineMessages({
      short: {
        defaultMessage: 'SHP',
      },
      long: {
        defaultMessage: 'Switch-Handed Pitcher',
      },
    }),
    [PreferredSide.LEFT]: defineMessages({
      short: {
        defaultMessage: 'LHP',
      },
      long: {
        defaultMessage: 'Left-Handed Pitcher',
      },
    }),
    [PreferredSide.RIGHT]: defineMessages({
      short: {
        defaultMessage: 'RHP',
      },
      long: {
        defaultMessage: 'Right-Handed Pitcher',
      },
    }),
  },
  batting: {
    [PreferredSide.BOTH]: defineMessages({
      short: {
        defaultMessage: 'S',
      },
      long: {
        defaultMessage: 'Switch-Handed Batter',
      },
    }),
    [PreferredSide.LEFT]: defineMessages({
      short: {
        defaultMessage: 'L',
      },
      long: {
        defaultMessage: 'Left-Handed Batter',
      },
    }),
    [PreferredSide.RIGHT]: defineMessages({
      short: {
        defaultMessage: 'R',
      },
      long: {
        defaultMessage: 'Right-Handed Batter',
      },
    }),
  },
};

type Props = {
  position: 'pitching' | 'batting';
  player: HandInfoChip_BaseballPlayer;
} & Pick<ComponentProps<typeof Chip>, 'size'>;

export const HandInfoChip = ({ position, player, size = 'S' }: Props) => {
  const handInfo = position === 'pitching' ? player.throwHand : player.batHand;
  if (!handInfo) return null;

  return (
    <Tooltip
      title={<FormattedMessage {...messages[position][handInfo].long} />}
    >
      <Chip size={size}>
        <FormattedMessage {...messages[position][handInfo].short} />
      </Chip>
    </Tooltip>
  );
};

HandInfoChip.fragments = {
  BaseballPlayer: gql`
    fragment HandInfoChip_BaseballPlayer on BaseballPlayer {
      slug
      batHand
      throwHand
    }
  ` as TypedDocumentNode<HandInfoChip_BaseballPlayer>,
};
